import GlobalLayout from 'layout';
import Head from 'next/head';

import HeroBlog from 'components/Hero/variants/Blog';

import ComponentGenerator from 'utils/builder/ComponentGenerator';
import { RenderBuilderComponents } from 'utils/builder/RenderBuilderContent';
import { fetchLayoutModels, getPageData } from 'utils/builder/builder.sdk';
import { getArticleSchema } from 'utils/schema/articleSchema';
import hasSchemaContent from 'utils/schema/hasSchemaContent';

import type { GetServerSideProps } from 'next';
import type { PageProps } from 'types/global';

const MODEL = 'blog';
const PATH = MODEL;
const FIELDS = [
  'id',
  'data.lastUpdated',
  'data.url',
  'data.blocks',
  'data.title',
  // Short SEO title from the Builder blog model; Head renders it as the <title>
  // tag (falling back to title) while the H1 keeps the full title. getPageData
  // currently ignores FIELDS, but keep this list accurate so the field is not
  // silently dropped if field selection is ever enforced.
  'data.seoTitle',
  'data.featuredImage',
  'data.summary',
  'data.tags',
  'data.author',
  'data.publishDate',
  'data.readTime',
  'data.breadcrumbTitle',
  'data.schemaJson',
].join(',');

const Blog = ({ pageData, layoutData, preview }: PageProps) => {
  const isInBuilderEditor = !!preview && !!pageData?.data?.isInBuilderEditor;

  // An editor-authored schemaJson on the Builder entry wins; otherwise generate
  // Article + Person schema from the post's own fields so every post is covered.
  // Builder seeds schemaJson as an empty `{}`, so guard on real content.
  const schemaJson = hasSchemaContent(pageData?.data?.schemaJson)
    ? pageData?.data?.schemaJson
    : getArticleSchema(pageData?.data);

  return (
    <>
      {schemaJson && (
        <Head>
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaJson) }} />
        </Head>
      )}
      <GlobalLayout preview={preview} seoData={pageData.data} {...layoutData}>
        <HeroBlog
          {...pageData.data}
          breadcrumbTitle={
            isInBuilderEditor && !pageData?.data?.breadcrumbTitle
              ? pageData?.data?.title
              : pageData?.data?.breadcrumbTitle
          }
          author={pageData?.data?.author || null}
          hideGradient
        />
        <ComponentGenerator>
          <RenderBuilderComponents
            model={MODEL}
            content={pageData}
            data={{ author: JSON.stringify(pageData?.data?.author) }}
          />
        </ComponentGenerator>
      </GlobalLayout>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ params, query, res, preview: draftPreview }) => {
  const slug = params?.slug as string;
  // BUILDER=1 is set on the production env too, so it cannot distinguish a real
  // visitor from the Builder.io editor / a shared preview link. The editor iframe
  // and Builder preview links carry `builder.space` / `builder.preview`, so gate
  // includeUnpublished on those: real visitors get published-only content (an
  // unpublished slug 404s instead of leaking) while a preview link renders the draft.
  const isBuilderEditor = !!query['builder.space'] || !!query['builder.preview'];
  const pageData = await getPageData(`${PATH}/${slug}`, MODEL, FIELDS, isBuilderEditor);
  const layoutData = await fetchLayoutModels(isBuilderEditor, !!draftPreview);

  if (!pageData?.data && !isBuilderEditor) {
    return { notFound: true };
  }

  // Edge-cache published content like the catch-all page route: browsers never
  // cache, the Vercel edge serves a shared 5-min snapshot and revalidates in the
  // background. Preview requests are never edge-cached so drafts stay private.
  res.setHeader('Cache-Control', 'private, no-cache, must-revalidate');
  // Draft-preview responses (Next.js Preview Mode) carry unpublished chrome,
  // so they must never seed the shared edge cache either.
  if (!isBuilderEditor && !draftPreview) {
    res.setHeader('CDN-Cache-Control', 'public, s-maxage=300, stale-while-revalidate=300');
  }

  return {
    props: { pageData: pageData || {}, layoutData: layoutData || {}, preview: isBuilderEditor },
  };
};

export default Blog;
